// « Paramètres » (#/parametres), opened from the avatar menu of the home. Appetite copycat world, grouped lists, vendored UBM
// Switch. Three blocks: « Mes notifications » (categories, reminders, channels), « Aide et contact », « Confidentialité et
// compte ». Everything is a session-local demo (compte-state.ts): no browser permission is requested, no e-mail, message,
// report, export or deletion is sent or performed. Legal links are the studio's real public pages (ACS footer, sourced).
import { ActionButton } from "@ubm/ui/components/action-button";
import { Button } from "@ubm/ui/components/button";
import { useState } from "react";
import { Switch } from "@ubm/ui/components/switch";
import { StatusBar, type Route } from "./parts";
import { MESSAGES } from "./home-fixtures";
import { account, useAccount, WEEKDAYS, type ConsentId, type NotifId, type PhoneChannel } from "./compte-state";
import { AccountGroup, AccountHeader, AccountRow, AccountSheet, useToast } from "./profil";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

const NOTIFS: { id: NotifId; label: string }[] = [
  { id: "messages", label: "Messages de mon coach" },
  { id: "programme", label: "Nouveau programme" },
  { id: "actualites", label: "Actualités et événements" },
  { id: "rappels", label: "Rappels de suivi" },
];
const CHANNEL: Record<PhoneChannel, string> = { active: "Activé", "a-autoriser": "À autoriser", bloque: "Bloqué" };
const CONSENTS: { id: ConsentId; label: string; detail: string }[] = [
  { id: "suivi", label: "Suivi d'entraînement", detail: "Tes séances, check-ins et habitudes, pour que ton coach suive ta progression." },
  { id: "sensibles", label: "Données de santé", detail: "Informations sensibles liées à ton accompagnement : sommeil, forme, mensurations." },
  { id: "photos", label: "Photos de progression", detail: "Les photos que tu ajoutes dans Mon suivi, visibles par ton coach." },
  { id: "marketing", label: "Utilisation de mes photos en communication", detail: "Facultatif. Le studio n'utilise une de tes photos dans sa communication (par exemple un avant / après) qu'avec cette autorisation. Distinct des photos de progression." },
];
// Real public pages of the studio site footer (sourced in docs/partners/acs-studio/couverture-legales.md).
const LEGAL = [
  { label: "Politique de confidentialité", href: "https://www.acs-studio.bzh/politique-de-confidentialite/" },
  { label: "Conditions générales de vente", href: "https://www.acs-studio.bzh/conditions-generales-de-vente/" },
  { label: "Mentions légales", href: "https://www.acs-studio.bzh/mentions-legales/" },
];
const GUIDES = [
  { title: "Réserver un cours", steps: ["Ouvre Réserver dans la barre du bas.", "Choisis un jour, puis un créneau.", "Confirme ; tu peux annuler depuis le même créneau."] },
  { title: "Faire ta séance", steps: ["Depuis l'accueil, ouvre Mon programme.", "Appuie sur Commencer, puis note charges et répétitions.", "Termine la séance : elle apparaît dans Mon suivi."] },
  { title: "Retrouver tes bilans", steps: ["Ouvre Mon suivi.", "Le bloc Mes bilans montre ton dernier bilan et son commentaire.", "« Tous mes bilans » affiche l'historique."] },
];

type Sheet = "reminders" | "phone" | "contact" | "guides" | "report" | "consents" | "legal" | "export" | "delete";

const sw = (checked: boolean, label: string, onChange: (v: boolean) => void) =>
  <Switch size="small" checked={checked} onCheckedChange={onChange} aria-label={label} />;

export function Parametres({ go }: PageProps) {
  const a = useAccount();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [toast, setToast] = useToast();
  // Local drafts of the sheets (reset when a sheet closes).
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [report, setReport] = useState("");
  const [capture, setCapture] = useState<string | null>(null);
  const [reported, setReported] = useState(false);
  const [deleteStep, setDeleteStep] = useState<"info" | "confirm">("info");
  const close = () => { setSheet(null); setMessage(""); setSent(false); setReport(""); setCapture(null); setReported(false); setDeleteStep("info"); };
  const remindersText = a.reminders.days.length ? `${a.reminders.days.join(", ")} · ${a.reminders.time.replace(":", " h ")}` : "Aucun jour";

  return <div className="cc-screen cc-account">
    <StatusBar />
    <div className="cc-scroll">
      <AccountHeader title="Paramètres" go={go} />

      {/* 1 · Mes notifications */}
      <AccountGroup title="Mes notifications" id="pa-notif">
        {NOTIFS.map(n => <AccountRow key={n.id} label={n.label} end={sw(a.notif[n.id], n.label, v => account.set({ notif: { ...a.notif, [n.id]: v } }))} />)}
        <AccountRow label="Jours et heure des rappels" value={a.notif.rappels ? remindersText : "Rappels coupés"} onClick={() => setSheet("reminders")} />
      </AccountGroup>
      <AccountGroup title="Recevoir les notifications" id="pa-canal">
        <AccountRow label="Sur mon téléphone" value={<span className="cc-account-pill" data-state={a.phoneChannel}>{CHANNEL[a.phoneChannel]}</span>} onClick={() => setSheet("phone")} />
        <AccountRow label="E-mail" end={sw(a.emailChannel, "Notifications par e-mail", v => account.set({ emailChannel: v }))} />
      </AccountGroup>

      {/* 2 · Aide et contact */}
      <AccountGroup title="Aide et contact" id="pa-aide">
        <AccountRow label="Contacter le studio" onClick={() => { window.location.hash = "#/messages"; }} />
        <AccountRow label="Comment utiliser l'application" onClick={() => setSheet("guides")} />
        <AccountRow label="Signaler un problème" onClick={() => setSheet("report")} />
      </AccountGroup>

      {/* 3 · Confidentialité et compte */}
      <AccountGroup title="Confidentialité et compte" id="pa-compte">
        <AccountRow label="Mes consentements" value={`${Object.values(a.consents).filter(Boolean).length} sur ${CONSENTS.length}`} onClick={() => setSheet("consents")} />
        <AccountRow label="Confidentialité et conditions" onClick={() => setSheet("legal")} />
        <AccountRow label="Télécharger mes données" value={a.exportRequested ? "Demande envoyée (démo)" : undefined} onClick={() => setSheet("export")} />
        <AccountRow label="Supprimer mon compte" onClick={() => setSheet("delete")} />
      </AccountGroup>

      <p className="cc-home-demo cc-account-demo">Prototype : réglages de démonstration gardés sur cet appareil le temps de la visite. Aucune autorisation demandée, rien n'est envoyé.</p>
    </div>

    {sheet === "reminders" && <AccountSheet title="Rappels de suivi" onClose={close}>
      <p className="cc-home-p">Choisis les jours et l'heure de tes rappels de check-in et d'habitudes.</p>
      <div className="cc-account-days" role="group" aria-label="Jours">
        {WEEKDAYS.map(d => { const on = a.reminders.days.includes(d); return <button key={d} type="button" className="cc-chip" aria-pressed={on} data-active={on || undefined}
          onClick={() => account.set({ reminders: { ...a.reminders, days: on ? a.reminders.days.filter(x => x !== d) : WEEKDAYS.filter(x => x === d || a.reminders.days.includes(x)) } })}>{d}</button>; })}
      </div>
      <label className="cc-account-field"><span>Heure</span><input type="time" value={a.reminders.time} onChange={e => account.set({ reminders: { ...a.reminders, time: e.target.value || a.reminders.time } })} /></label>
      {!a.notif.rappels && <p className="cc-account-note">Les rappels de suivi sont coupés : ces réglages s'appliqueront quand tu les réactiveras.</p>}
      <ActionButton className="cc-cta" label="Enregistrer" pendingLabel="Enregistrement" successLabel="Rappels enregistrés" onAction={async () => { await new Promise<void>(r => window.setTimeout(r, 450)); window.setTimeout(close, 700); }} />
    </AccountSheet>}

    {sheet === "phone" && <AccountSheet title="Notifications sur ce téléphone" onClose={close}>
      <p className="cc-home-p">Dans l'application réelle, ce réglage suit l'autorisation donnée au téléphone. Ici, tu peux simuler chaque état ; aucune autorisation n'est demandée.</p>
      <div className="cc-seg" role="radiogroup" aria-label="État de démonstration">
        {(Object.keys(CHANNEL) as PhoneChannel[]).map(k => <button key={k} type="button" role="radio" aria-checked={a.phoneChannel === k} data-active={a.phoneChannel === k || undefined} onClick={() => account.set({ phoneChannel: k })}>{CHANNEL[k]}</button>)}
      </div>
      <p className="cc-account-note">{a.phoneChannel === "active" ? "Tu reçois les notifications choisies sur ce téléphone." : a.phoneChannel === "a-autoriser" ? "Le téléphone te demandera ton accord la première fois." : "Les notifications sont bloquées dans les réglages du téléphone ; tu peux les réactiver depuis ces réglages."}</p>
    </AccountSheet>}

    {sheet === "guides" && <AccountSheet title="Comment utiliser l'application" onClose={close}>
      {GUIDES.map(g => <div key={g.title} className="cc-account-guide"><p className="cc-account-row-label">{g.title}</p><ol className="cc-home-ex">{g.steps.map(s => <li key={s}>{s}</li>)}</ol></div>)}
    </AccountSheet>}

    {sheet === "report" && <AccountSheet title="Signaler un problème" onClose={close}>
      {reported ? <p className="cc-account-ok" role="status">Merci, signalement enregistré · démo, rien n'est transmis.</p> : <>
        <label className="cc-account-field"><span>Que s'est-il passé ?</span><textarea rows={4} value={report} onChange={e => setReport(e.target.value)} placeholder="Décris le problème et l'écran concerné" /></label>
        <label className="cc-account-file"><span>{capture ? `Capture : ${capture}` : "Ajouter une capture (facultatif)"}</span>
          <input type="file" accept="image/*" onChange={e => setCapture(e.target.files?.[0]?.name ?? null)} /></label>
        <Button variant="primary" size="lg" className="cc-cta" disabled={!report.trim()} onClick={() => setReported(true)}>Envoyer</Button>
      </>}
    </AccountSheet>}

    {sheet === "consents" && <AccountSheet title="Mes consentements" onClose={close}>
      <p className="cc-home-p">Chaque consentement est distinct ; tu peux le modifier à tout moment.</p>
      <div className="cc-account-list cc-account-list--flat">{CONSENTS.map(c => <div key={c.id} className="cc-account-consent">
        <span><span className="cc-account-row-label">{c.label}</span><small>{c.detail}</small></span>
        {sw(a.consents[c.id], c.label, v => account.set({ consents: { ...a.consents, [c.id]: v } }))}
      </div>)}</div>
      <a className="cc-suivi-link" href={LEGAL[0]!.href} target="_blank" rel="noopener noreferrer">Lire la politique de confidentialité</a>
    </AccountSheet>}

    {sheet === "legal" && <AccountSheet title="Confidentialité et conditions" onClose={close}>
      <div className="cc-account-list cc-account-list--flat">{LEGAL.map(l => <a key={l.href} className="cc-account-row" href={l.href} target="_blank" rel="noopener noreferrer"><span className="cc-account-row-label">{l.label}</span><span className="cc-account-row-value">acs-studio.bzh</span></a>)}</div>
    </AccountSheet>}

    {sheet === "export" && <AccountSheet title="Télécharger mes données" onClose={close}>
      {a.exportRequested ? <p className="cc-account-ok" role="status">Demande enregistrée · démo : aucun fichier n'est généré.</p> : <>
        <p className="cc-home-p">Tu peux demander une copie des données de ton compte. Dans l'application réelle, la demande serait transmise au studio.</p>
        <Button variant="primary" size="lg" className="cc-cta" onClick={() => account.set({ exportRequested: true })}>Demander l'export</Button>
      </>}
    </AccountSheet>}

    {sheet === "delete" && <AccountSheet title="Supprimer mon compte" onClose={close}>
      {a.deletionRequested ? <p className="cc-account-ok" role="status">Demande simulée · démo : aucun compte ni aucune donnée n'est supprimé.</p>
        : deleteStep === "info" ? <>
          <p className="cc-account-row-label">Ce qui se passe</p>
          <ul className="cc-account-bullets">
            <li>Ton accès à l'espace membre est fermé.</li>
            <li>Ton suivi, tes séances et tes bilans ne sont plus consultables.</li>
            <li>Ce que le studio conserve, et combien de temps, est décrit dans sa <a href={LEGAL[0]!.href} target="_blank" rel="noopener noreferrer">politique de confidentialité</a>.</li>
          </ul>
          <p className="cc-account-note">Supprimer ton compte et résilier un abonnement sont deux démarches distinctes : pour ton abonnement, contacte le studio.</p>
          <Button variant="danger" size="lg" className="cc-cta cc-account-danger" onClick={() => setDeleteStep("confirm")}>Continuer</Button>
        </> : <>
          <p className="cc-home-p">Confirmer la demande de suppression de ton compte ?</p>
          <Button variant="danger" size="lg" className="cc-cta cc-account-danger" onClick={() => account.set({ deletionRequested: true })}>Demander la suppression</Button>
          <Button variant="ghost" size="sm" onClick={close}>Annuler</Button>
        </>}
    </AccountSheet>}
    {toast}
  </div>;
}
